/**
 * CreateItemPoolModal — shared "New Item Pool" dialog.
 * Used by Priority Lists, the Item Pool Editor, and mobile Priorities.
 *
 * A scope choice is required. Canon Doc 03 §2.5 / §2.11A: an Item Pool is the
 * explicit comparison set chosen before ranking, scoped to a Track / Cycle
 * **at lock**. The dialog offers the cycles and standalone tracks, defaulting
 * to the pinned cycle, plus an explicit "Not yet" for a draft assembled before
 * its cycle exists (Doc 04: pools can be created before sessions). The store
 * refuses to lock an unscoped pool. Pure scope rules: src/lib/poolScope.ts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Plus, X } from 'lucide-react';
import { PoolScopeFields } from './PoolScopeFields';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { usePinnedCycleStore } from '../../stores/prioritization/pinnedCycleStore';
import {
  buildPoolScopeOptions,
  defaultPoolScopeKey,
  parsePoolScopeKey,
  poolScopeKeyForCycle,
  poolScopeKeyForTrack,
  resolvePoolScope,
  tracksForCycle,
  POOL_SCOPE_REQUIRED_MESSAGE,
  findPoolOnTrack,
  poolPath,
  trackAlreadyHasPoolMessage,
  type PoolScopeKey,
} from '../../lib/poolScope';

interface CreateItemPoolModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  /** Open with this cycle selected (falls back to the pinned cycle). */
  defaultCycleId?: string | null;
  /** Open with this track selected inside the default cycle, or as a standalone track. */
  defaultTrackId?: string | null;
}

const FIELD_CLASS =
  'w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:border-[var(--slate)] focus:ring-1 focus:ring-indigo-500 outline-none transition-colors';

export function CreateItemPoolModal({
  open,
  onClose,
  onCreated,
  defaultCycleId,
  defaultTrackId,
}: CreateItemPoolModalProps) {
  const createItemPool = useItemPoolsStore((s) => s.createItemPool);
  const existingPools = useItemPoolsStore((s) => s.itemPools);
  const userId = useUserStore((s) => s.userId) || 'anonymous';

  const cycles = useDegapProcessStore((s) => s.cycles);
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const tracks = useTrackStore((s) => s.tracks);
  const fetchAllTracks = useTrackStore((s) => s.fetchAllTracks);
  const pinnedCycleId = usePinnedCycleStore((s) => s.pinnedCycleId);
  const pinnedTrackId = usePinnedCycleStore((s) => s.pinnedTrackId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeKey, setScopeKey] = useState<PoolScopeKey>('');
  const [trackId, setTrackId] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  const options = useMemo(() => buildPoolScopeOptions(cycles, tracks), [cycles, tracks]);
  const cycleTracks = useMemo(() => {
    const parsed = parsePoolScopeKey(scopeKey);
    return parsed?.kind === 'cycle' ? tracksForCycle(tracks, parsed.id) : [];
  }, [scopeKey, tracks]);

  // Load the scope vocabulary the first time the dialog opens in a session.
  useEffect(() => {
    if (!open) return;
    if (cycles.length === 0) void fetchCycles();
    if (tracks.length === 0) void fetchAllTracks();
    // Only on open: the stores refetching on every render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset the form on open, seeded from the caller's default, then the pinned cycle.
  useEffect(() => {
    if (!open) return;
    setName('');
    setDescription('');
    setError('');
    setCreating(false);
    const seedCycle = defaultCycleId ?? pinnedCycleId ?? null;
    const seedTrack = defaultTrackId ?? pinnedTrackId ?? '';
    const standalone = seedTrack ? tracks.find((t) => t.id === seedTrack && t.cycleId === null) : undefined;
    if (standalone) {
      setScopeKey(poolScopeKeyForTrack(standalone.id));
      setTrackId('');
    } else if (seedCycle && options.cycles.some((c) => c.id === seedCycle)) {
      setScopeKey(poolScopeKeyForCycle(seedCycle));
      setTrackId(seedTrack);
    } else {
      setScopeKey(defaultPoolScopeKey(options, null));
      setTrackId('');
    }
    setTimeout(() => nameRef.current?.focus(), 50);
    // Seeding is an on-open concern; later option loads are handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If the options arrive after open (first fetch), fill in a default scope once.
  useEffect(() => {
    if (!open || scopeKey) return;
    const next = defaultPoolScopeKey(options, defaultCycleId ?? pinnedCycleId ?? null);
    if (next) setScopeKey(next);
  }, [open, scopeKey, options, defaultCycleId, pinnedCycleId]);

  // Keep the track pick valid for the chosen cycle.
  useEffect(() => {
    if (cycleTracks.length === 0) {
      if (trackId) setTrackId('');
      return;
    }
    if (!cycleTracks.some((t) => t.id === trackId)) setTrackId(cycleTracks[0].id);
  }, [cycleTracks, trackId]);

  // One pool per track: refuse up front, and point at the pool that is already there.
  const poolOnTrack = useMemo(() => {
    const scope = resolvePoolScope(scopeKey, tracks, trackId);
    return findPoolOnTrack(existingPools ?? [], scope?.trackId);
  }, [existingPools, scopeKey, tracks, trackId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError('A name is required.');
        return;
      }
      const scope = resolvePoolScope(scopeKey, tracks, trackId);
      if (!scope) {
        setError(POOL_SCOPE_REQUIRED_MESSAGE);
        return;
      }
      if (findPoolOnTrack(existingPools ?? [], scope.trackId)) return; // the notice below says why
      setCreating(true);
      setError('');
      try {
        const id = await createItemPool({
          name: trimmed,
          description: description.trim() || undefined,
          ownerId: userId,
          comparisonSetId: '',
          criteriaIds: [],
          cutLinePosition: 0,
          status: 'draft',
          tags: [],
          ...scope,
        } as Parameters<typeof createItemPool>[0]);
        onCreated(id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create item pool.');
        setCreating(false);
      }
    },
    [name, description, userId, scopeKey, trackId, tracks, existingPools, createItemPool, onCreated],
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] shadow-2xl w-full max-w-md border border-[var(--rule)] dark:border-[var(--rule-bright)]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)]">
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">New Item Pool</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-sec)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              ref={nameRef}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              placeholder="e.g. Q3 Engineering Priorities"
              className={FIELD_CLASS}
              disabled={creating}
            />
          </div>

          <PoolScopeFields
            cycles={cycles}
            tracks={tracks}
            scopeKey={scopeKey}
            trackId={trackId}
            onScopeKeyChange={(key) => {
              setScopeKey(key);
              setError('');
            }}
            onTrackIdChange={setTrackId}
            allowNone
            disabled={creating}
            idPrefix="create-pool"
          />

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Description <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this pool for?"
              rows={3}
              className={`${FIELD_CLASS} resize-none`}
              disabled={creating}
            />
          </div>

          {poolOnTrack && (
            <p className="text-sm text-[var(--coral)]" role="alert">
              {trackAlreadyHasPoolMessage(poolOnTrack.name)}.{' '}
              <Link to={poolPath(poolOnTrack.id)} onClick={onClose} className="underline underline-offset-2">
                Open it
              </Link>{' '}
              or choose a different track.
            </p>
          )}

          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] bg-[var(--bg-raised)] dark:bg-transparent border border-[var(--rule)] dark:border-[var(--rule-bright)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim() || !scopeKey || !!poolOnTrack}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Creating…
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" />
                  Create Pool
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
